import React, { useRef, useEffect } from 'react';
import { Canvas, useFrame, useLoader } from '@react-three/fiber';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import * as THREE from 'three';

const AnimatedLogo = () => {
  const meshRef = useRef();
  const gltf = useLoader(GLTFLoader, '/flux_logo.glb');

  // Add glow effect to materials
  useEffect(() => {
    if (gltf.scene) {
      gltf.scene.traverse((child) => {
        if (child.isMesh) {
          // Make material emissive for glow effect
          child.material.emissive = new THREE.Color(0x6F3AFF);
          child.material.emissiveIntensity = 0.3;
          // Add some shine
          child.material.metalness = 0.8;
          child.material.roughness = 0.2;
        }
      });
    }
  }, [gltf]);

  useFrame((state, delta) => {
    if (meshRef.current) {
      // Oscillate sideways between -90° and +90° (in radians: -π/2 to π/2)
      meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * (Math.PI / 3);
      // Add subtle floating animation
      meshRef.current.position.y = Math.sin(state.clock.elapsedTime) * 0.05;
      //starting rotation
    }
  });

  return (
    <primitive
      ref={meshRef}
      object={gltf.scene}
      scale={3}
      position={[0, 0, 0]}
    />
  );
};

const AnimatedLights = () => {
  const pointLight1 = useRef();
  const pointLight2 = useRef();
  const directionalLight = useRef();

  useFrame((state, delta) => {
    const time = state.clock.elapsedTime;
    
    if (pointLight1.current) {
      // First light orbits around
      pointLight1.current.position.x = Math.cos(time * 0.7) * 8;
      pointLight1.current.position.z = Math.sin(time * 0.7) * 8;
      pointLight1.current.position.y = Math.sin(time * 0.4) * 3 + 2;
    }
    
    if (pointLight2.current) {
      // Second light moves in opposite direction
      pointLight2.current.position.x = Math.cos(time * -0.5) * 6;
      pointLight2.current.position.z = Math.sin(time * -0.5) * 6;
      pointLight2.current.position.y = Math.cos(time * 0.6) * 2 + 1;
    }
    
    if (directionalLight.current) {
      // Directional light slowly rotates
      directionalLight.current.position.x = Math.cos(time * 0.3) * 10;
      directionalLight.current.position.y = 8;
      directionalLight.current.position.z = Math.sin(time * 0.3) * 10;
    }
  });

  return (
    <>
      <pointLight 
        ref={pointLight1}
        intensity={2}
        color="#6F3AFF"
        distance={20}
      />
      <pointLight 
        ref={pointLight2}
        intensity={1.5}
        color="#00F0FF"
        distance={15}
      />
      <directionalLight 
        ref={directionalLight}
        intensity={0.8}
        color="#ffffff"
      />
    </>
  );
};

const Logo3D = ({ className = "" }) => {
  return (
    <div className={`w-[80vw] max-w-[280px] md:max-w-[360px] h-[80vw] max-h-[280px] md:max-h-[360px] ${className}`}>
      <Canvas
        camera={{
          position: [0, 0, 5],
          fov: 50,
        }}
        style={{
          width: '100%',
          height: '100%',
        }}
      >
        <ambientLight intensity={0.2} />
        <AnimatedLights />
        <React.Suspense fallback={null}>
          <AnimatedLogo />
        </React.Suspense>
      </Canvas>
    </div>
  );
};

export default Logo3D;
